import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import styles from './CookieConsent.module.css';

const COOKIE_CONSENT_KEY = 'polaris_cookie_consent';

interface CookiePreferences {
  essential: boolean; // Always true, required
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

export function CookieConsent() {
  const { language } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    timestamp: 0,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      setShowBanner(true);
    } else {
      try {
        const parsed = JSON.parse(stored) as CookiePreferences;
        setPreferences(parsed);
      } catch {
        setShowBanner(true);
      }
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    const withTimestamp = { ...prefs, timestamp: Date.now() };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(withTimestamp));
    setPreferences(withTimestamp);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    });
  };

  const rejectAll = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    });
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  const t = {
    tr: {
      title: 'Çerez Kullanımı',
      description: 'Deneyiminizi iyileştirmek için çerezler kullanıyoruz. Zorunlu çerezler site işleyişi için gereklidir. Analitik ve pazarlama çerezleri için onayınızı istiyoruz.',
      acceptAll: 'Tümünü Kabul Et',
      rejectAll: 'Tümünü Reddet',
      customize: 'Özelleştir',
      savePreferences: 'Tercihleri Kaydet',
      essential: 'Zorunlu Çerezler',
      essentialDesc: 'Site işleyişi için gerekli. Devre dışı bırakılamaz.',
      analytics: 'Analitik Çerezler',
      analyticsDesc: 'Site kullanımını anlamamıza yardımcı olur.',
      marketing: 'Pazarlama Çerezleri',
      marketingDesc: 'Kişiselleştirilmiş reklamlar için kullanılır.',
      learnMore: 'Çerez Politikası',
      manageConsent: 'Çerez Tercihlerini Yönet',
    },
    en: {
      title: 'Cookie Usage',
      description: 'We use cookies to improve your experience. Essential cookies are required for site functionality. We ask for your consent for analytics and marketing cookies.',
      acceptAll: 'Accept All',
      rejectAll: 'Reject All',
      customize: 'Customize',
      savePreferences: 'Save Preferences',
      essential: 'Essential Cookies',
      essentialDesc: 'Required for site functionality. Cannot be disabled.',
      analytics: 'Analytics Cookies',
      analyticsDesc: 'Help us understand site usage.',
      marketing: 'Marketing Cookies',
      marketingDesc: 'Used for personalized advertising.',
      learnMore: 'Cookie Policy',
      manageConsent: 'Manage Cookie Preferences',
    },
  };

  const text = t[language];

  if (!showBanner && !showPreferences) {
    return (
      <button
        className={styles.manageCookiesBtn}
        onClick={() => setShowPreferences(true)}
        title={text.manageConsent}
        aria-label={text.manageConsent}
      >
        🍪
      </button>
    );
  }

  return (
    <>
      {/* Overlay */}
      <div className={styles.overlay} onClick={() => showPreferences && setShowPreferences(false)} />

      {/* Main Banner */}
      {showBanner && !showPreferences && (
        <div className={styles.banner} role="dialog" aria-labelledby="cookie-title">
          <div className={styles.bannerContent}>
            <div className={styles.textSection}>
              <h3 id="cookie-title" className={styles.title}>{text.title}</h3>
              <p className={styles.description}>{text.description}</p>
              <Link to="/cookie-policy" className={styles.learnMore}>
                {text.learnMore} →
              </Link>
            </div>

            <div className={styles.buttonSection}>
              {/* GDPR Compliant: Equal buttons, no dark patterns */}
              <button onClick={rejectAll} className={styles.primaryBtn}>
                {text.rejectAll}
              </button>
              <button onClick={acceptAll} className={styles.primaryBtn}>
                {text.acceptAll}
              </button>
              <button onClick={() => setShowPreferences(true)} className={styles.secondaryBtn}>
                {text.customize}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div className={styles.modal} role="dialog" aria-labelledby="prefs-title">
          <div className={styles.modalContent}>
            <h3 id="prefs-title" className={styles.modalTitle}>{text.manageConsent}</h3>

            <div className={styles.cookieCategory}>
              <div className={styles.categoryHeader}>
                <label className={styles.categoryLabel}>
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className={styles.checkbox}
                  />
                  <span className={styles.categoryName}>{text.essential}</span>
                </label>
                <span className={styles.requiredBadge}>
                  {language === 'tr' ? 'Zorunlu' : 'Required'}
                </span>
              </div>
              <p className={styles.categoryDesc}>{text.essentialDesc}</p>
            </div>

            <div className={styles.cookieCategory}>
              <div className={styles.categoryHeader}>
                <label className={styles.categoryLabel}>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className={styles.checkbox}
                  />
                  <span className={styles.categoryName}>{text.analytics}</span>
                </label>
              </div>
              <p className={styles.categoryDesc}>{text.analyticsDesc}</p>
            </div>

            <div className={styles.cookieCategory}>
              <div className={styles.categoryHeader}>
                <label className={styles.categoryLabel}>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className={styles.checkbox}
                  />
                  <span className={styles.categoryName}>{text.marketing}</span>
                </label>
              </div>
              <p className={styles.categoryDesc}>{text.marketingDesc}</p>
            </div>

            <div className={styles.modalButtons}>
              <button onClick={rejectAll} className={styles.primaryBtn}>
                {text.rejectAll}
              </button>
              <button onClick={acceptAll} className={styles.primaryBtn}>
                {text.acceptAll}
              </button>
              <button onClick={saveCustom} className={styles.saveBtn}>
                {text.savePreferences}
              </button>
            </div>

            <Link to="/cookie-policy" className={styles.modalLink}>
              {text.learnMore}
            </Link>
          </div>
        </div>
      )}
    </>
  );
}

// Hook to check cookie consent status
export function useCookieConsent(): CookiePreferences | null {
  const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as CookiePreferences;
  } catch {
    return null;
  }
}
